import styles from './book-summary.module.scss';
import { Typography } from '@sanlam-glacier/app-common/components';
import { useCountUp } from 'hooks/useCountUp';
import { Suspense, useContext, useEffect } from 'react';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import WidgetError from '../../../../atoms/widget-error/WidgetError';
import { useQuery } from '@tanstack/react-query';
import { getBookSummary } from 'bff/hooks/reporting/BookSummaryApi';
import { useFormatCurrency } from 'modules/dashboard/utils/formatting/useFormatCurrency';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';

export const BookSummaryWidget = () => {
  const context = useContext(WidgetContext);

  const {
    data: bookSummaryData,
    isFetching,
    isError,
    refetch,
    isSuccess
  } = useQuery(getBookSummary({ intermediaryId: 1 }));

  //Todo: remove, post testing
  const testingBookValue = 599999;
  const testingClients = 7;
  const testingInvestments = 33;

  const formattedTotalValue = useCountUp(
    bookSummaryData?.totalBookValue ? bookSummaryData.totalBookValue + testingBookValue : 0
  );
  const { formatCurrency, formatNumber } = useFormatCurrency();
  const testComponentName = 'bookSummary';

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, []);

  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  return (
    <Suspense fallback={<WidgetLoader />}>
      {isFetching ? (
        <WidgetLoader />
      ) : isError ? (
        <WidgetError onRefresh={refetch} isRefreshing={isFetching} />
      ) : (
        <div className={styles.bookSummary}>
          <div className={styles.bookSummaryContent}>
            <div className={styles.bookSummaryTotal}>
              <div className={styles.valueContainer}>
                <Typography
                  variant={Typography.VARIANT.B3}
                  weight={Typography.WEIGHT.MEDIUM}
                  className={styles.totalValueHeading}
                  data-testid={testComponentName + 'TotalBookValueLabel'}
                >
                  Total book value
                </Typography>
                <Typography
                  variant={Typography.VARIANT.H2}
                  weight={Typography.WEIGHT.MEDIUM}
                  className={styles.totalValueNumber}
                  data-testid={testComponentName + 'TotalBookValueValue'}
                >
                  {`R ${formattedTotalValue}`}
                </Typography>
              </div>
            </div>

            <div className={styles.colContainer}>
              <div className={styles.bookSummaryCol}>
                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.breakdownHeading}
                    data-testid={testComponentName + 'AvgPortfolioValueLabel'}
                  >
                    Average portfolio value
                  </Typography>
                </div>

                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.breakdownHeading}
                    data-testid={testComponentName + 'AvgInvestmentValueLabel'}
                  >
                    Average investment value
                  </Typography>
                </div>

                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.breakdownHeading}
                    data-testid={testComponentName + 'NumberOfClientLabel'}
                  >
                    Number of clients
                  </Typography>
                </div>

                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.NORMAL}
                    className={styles.breakdownHeading}
                    data-testid={testComponentName + 'NumberOfInvestmentsLabel'}
                  >
                    Number of investments
                  </Typography>
                </div>
              </div>

              <div className={styles.bookSummaryCol}>
                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={styles.breakdownAmount}
                    data-testid={testComponentName + 'AvgPortfolioValue'}
                  >
                    {formatCurrency(bookSummaryData?.averagePortfolioValue)}
                  </Typography>
                </div>
                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={styles.breakdownAmount}
                    data-testid={testComponentName + 'AvgInvestmentValue'}
                  >
                    {formatCurrency(bookSummaryData?.averageInvestmentValue)}
                  </Typography>
                </div>

                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={styles.breakdownAmount}
                    data-testid={testComponentName + 'NumberOfClients'}
                  >
                    {formatNumber((bookSummaryData?.numberOfClients ?? 0) + testingClients)}
                  </Typography>
                </div>

                <div className={styles.bookSummaryRow}>
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={styles.breakdownAmount}
                    data-testid={testComponentName + 'NumberOfInvestments'}
                  >
                    {formatNumber((bookSummaryData?.numberOfInvestments ?? 0) + testingInvestments)}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Suspense>
  );
};

export default BookSummaryWidget;
